// Convert structured formulas + material composition data into LaTeX
// suitable for KaTeX rendering.
//
// The codification rule:
//   - If the material has a `composition` list, render as the chemical
//     formula with subscript stoichiometric counts: `H_2O`, `Fe_2O_3`.
//   - Otherwise fall back to the human-readable name in roman type:
//     `\mathrm{Sand}`.
// The output uses `\mathrm{}` so units / element symbols are upright,
// per ACS / IUPAC convention for chemical formulas in equations.

import type { MaterialDef } from "../sim/types";

import type { Formula, FormulaTerm, MaterialResolver } from "./types";

interface CompositionEntry {
  id: string;
  n: number;
}

function compositionOf(material: MaterialDef): CompositionEntry[] | undefined {
  // Loosely typed because the YAML parser produces structurally compatible
  // objects but not strict typed instances.
  const composition = (material as unknown as { composition?: unknown[] }).composition;
  if (!Array.isArray(composition)) return undefined;
  const entries: CompositionEntry[] = [];
  for (const c of composition) {
    if (!c || typeof c !== "object") continue;
    const obj = c as { id?: unknown; n?: unknown };
    if (typeof obj.id !== "string") continue;
    const n = typeof obj.n === "number" ? obj.n : 1;
    entries.push({ id: obj.id, n });
  }
  return entries.length > 0 ? entries : undefined;
}

function escapeLatex(s: string): string {
  return s.replace(/[\\{}#%&_~^]/g, "\\$&");
}

function nameToLatex(material: MaterialDef): string {
  const cleaned = material.name.replace(/\s+/g, "\\,");
  return `\\mathrm{${escapeLatex(cleaned)}}`;
}

function compositionToLatex(entries: CompositionEntry[]): string {
  return entries
    .map((e) => {
      const sym = escapeLatex(e.id);
      // Integer-looking counts go bare; non-integer counts (alloys) get
      // a decimal subscript.
      if (e.n === 1) return `\\mathrm{${sym}}`;
      const display = Number.isInteger(e.n) ? String(e.n) : e.n.toFixed(2);
      return `\\mathrm{${sym}}_{${display}}`;
    })
    .join("");
}

export function materialToLatex(
  id: string,
  resolver: MaterialResolver,
): string {
  const material = resolver.byId.get(id);
  if (!material) {
    return `\\mathrm{?${escapeLatex(id)}?}`;
  }
  const composition = compositionOf(material);
  if (composition) {
    return compositionToLatex(composition);
  }
  return nameToLatex(material);
}

function termToLatex(term: FormulaTerm, resolver: MaterialResolver): string {
  const body = materialToLatex(term.id, resolver);
  return term.n === 1 ? body : `${term.n}\\,${body}`;
}

function sideToLatex(terms: FormulaTerm[], resolver: MaterialResolver): string {
  return terms.map((t) => termToLatex(t, resolver)).join(" + ");
}

/** Render a parsed Formula to a KaTeX-ready LaTeX string. */
export function formulaToLatex(formula: Formula, resolver: MaterialResolver): string {
  const lhs = sideToLatex(formula.reactants, resolver);
  const rhs = sideToLatex(formula.products, resolver);
  const arrow = formula.conditions.temperatureK !== undefined
    ? `\\xrightarrow{${formula.conditions.temperatureK}\\,\\mathrm{K}}`
    : "\\rightarrow";
  return `${lhs} ${arrow} ${rhs}`;
}

/** Build a [`MaterialResolver`] from a list of `MaterialDef`. */
export function buildResolver(materials: MaterialDef[]): MaterialResolver {
  return {
    byId: new Map(materials.map((m) => [m.id, m])),
  };
}
